import {
  routes,
  type RequestTypeOf,
  type ResponseTypeOf,
  type RouteName,
} from "../generated/routes";
import { fetchWithToken } from "../utils/fetchWithToken";

type ExtraOptions = {
  headers?: Record<string, string>;
  params?: Record<string, string | number>;
  // Paramètres de query string (?a=1&b=2). Une valeur `undefined` est ignorée,
  // ce qui permet d'écrire `{ status, role }` sans tester chaque filtre.
  query?: Record<string, string | number | undefined>;
};

export const buildPath = (path: string, params?: ExtraOptions["params"]) => {
  if (!params) return path;
  return path.replace(/\{([^}]+)\}/g, (_, key) => {
    if (!(key in params)) {
      throw new Error(`Missing param "${key}" for path "${path}"`);
    }
    return encodeURIComponent(String(params[key]));
  });
};

export const buildQuery = (query?: ExtraOptions["query"]) => {
  if (!query) return "";
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      search.append(key, String(value));
    }
  });
  const asString = search.toString();
  return asString ? `?${asString}` : "";
};

export async function apiFetchRoute<K extends RouteName>(
  routeName: K,
  body?: RequestTypeOf<K>,
  options: ExtraOptions = {}
): Promise<Response> {
  const { method, path, requiresToken } = routes[routeName];
  const resolvedPath = buildPath(path, options.params) + buildQuery(options.query);

  const isGet = method === "GET";
  const mergedHeaders: Record<string, string> = {
    "Content-type": "application/json",
    ...(options.headers || {}),
  };

  return fetchWithToken(
    `${process.env.REACT_APP_URL_SERVER_DATA}${resolvedPath}`,
    {
      method,
      headers: mergedHeaders,
      body: isGet || body === undefined ? undefined : JSON.stringify(body),
      requireAuth: requiresToken,
    }
  );
}

export async function apiFetchJson<K extends RouteName>(
  routeName: K,
  body?: RequestTypeOf<K>,
  options: ExtraOptions = {}
): Promise<ResponseTypeOf<K>> {
  const res = await apiFetchRoute(routeName, body, options);
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  if (res.status === 204) {
    return undefined as ResponseTypeOf<K>;
  }

  // Un 200 SANS CORPS est courant côté DATA : `SendStatusToFront(w, http.StatusOK)`
  // écrit le statut et rien d'autre (28 handlers le font). `res.json()` lèverait alors
  // « Unexpected end of JSON input », et l'appelant, qui attrape tout dans un `catch`,
  // transformerait un SUCCÈS en message d'erreur — l'action ayant bien eu lieu côté
  // serveur. Symptôme observé sur le retrait de licence : erreur affichée, licence
  // pourtant retirée, état correct après réouverture de l'écran.
  //
  // On décode donc en tolérant l'absence de corps, plutôt qu'en supposant du JSON.
  //
  // `res.text()` est privilégié (il permet de distinguer « vide » de « JSON invalide »),
  // avec un repli sur `res.json()` : un Response peut être simulé sans `text` dans les
  // tests, et un fetch polyfillé peut ne pas l'exposer.
  try {
    if (typeof res.text === "function") {
      const raw = await res.text();
      return raw.trim() === ""
        ? (undefined as ResponseTypeOf<K>)
        : (JSON.parse(raw) as ResponseTypeOf<K>);
    }
    return (await res.json()) as ResponseTypeOf<K>;
  } catch {
    // Corps absent ou illisible sur une réponse OK : l'action a réussi, il n'y a
    // simplement rien à lire. Lever ici ferait passer un SUCCÈS pour une erreur —
    // le bug d'origine.
    return undefined as ResponseTypeOf<K>;
  }
}
